import { Flex } from '@sequencehq/core-components'
import { useAtomValue } from 'jotai'
import { match } from 'ts-pattern'
import { useQuery } from '@sequencehq/api/utils'
import { dashboardv99990101Client } from '@sequencehq/api/dashboard/v99990101'
import { MINUTES } from '@sequencehq/utils/dates'
import {
  invoiceIdAtom,
  invoiceStatusAtom
} from 'Invoices/atoms/invoiceEditing.atom'
import {
  ButtonClose,
  ButtonDownloadPdf,
  ButtonTogglePreview,
  FinalizeAndSendModal
} from 'Invoices/components/CreateEditShared'
import {
  ButtonRecalculateInvoice,
  ButtonSendInvoice,
  ButtonSendPaymentReminder,
  ButtonViewInCustomerPortal,
  DropdownUpdatePaymentStatus
} from './actions'
import { KebabMenu } from './KebabMenu'
import { toEligibilityVerdict } from './eligibility'
import { ButtonReviewAndSend } from './ButtonReviewAndSend'
import { LastRecalculated } from './LastRecalculated'

export const HeaderActions = () => {
  const status = useAtomValue(invoiceStatusAtom)
  const invoiceId = useAtomValue(invoiceIdAtom)

  // Both endpoints fold in their own feature flag plus ERP/PSP checks, so the
  // verdicts are resolved here and the actions branch synchronously on a prop.
  const cnV2EligibilityQuery = useQuery(
    dashboardv99990101Client.getCreditNoteApplicationEligibility,
    { invoiceId: invoiceId ?? '' },
    { staleTime: 1 * MINUTES, enabled: Boolean(invoiceId) }
  )
  const manualPaymentEligibilityQuery = useQuery(
    dashboardv99990101Client.getManualPaymentEligibility,
    { invoiceId: invoiceId ?? '' },
    { staleTime: 1 * MINUTES, enabled: Boolean(invoiceId) }
  )

  const cnV2Eligibility = toEligibilityVerdict(cnV2EligibilityQuery.data)
  const manualPaymentEligibility = toEligibilityVerdict(
    manualPaymentEligibilityQuery.data
  )

  if (!status) return null

  return (
    <Flex gap="8px" alignItems="center">
      {match(status)
        .with('DRAFT', () => {
          return (
            <>
              <LastRecalculated />
              <ButtonRecalculateInvoice />
              <ButtonDownloadPdf variant="icon" />
              <ButtonTogglePreview />
              <ButtonFinalizeAndSend />
            </>
          )
        })
        .with('IN_PROGRESS', () => {
          return (
            <>
              <LastRecalculated />
              <ButtonRecalculateInvoice />
              <ButtonDownloadPdf variant="icon" />
              <ButtonTogglePreview />
            </>
          )
        })
        .with('FINAL', () => {
          return (
            <>
              <ButtonDownloadPdf variant="icon" />
              <ButtonTogglePreview />
              <ButtonSendInvoice />
            </>
          )
        })
        .with('SENT', () => {
          return (
            <>
              <ButtonDownloadPdf variant="icon" />
              <ButtonViewInCustomerPortal />
              <ButtonTogglePreview />
              <DropdownUpdatePaymentStatus
                manualPaymentEligibility={manualPaymentEligibility}
              />
              <ButtonSendPaymentReminder />
            </>
          )
        })
        .with('VOIDED', () => {
          return (
            <>
              <ButtonDownloadPdf variant="icon" />
              <ButtonTogglePreview />
            </>
          )
        })
        .exhaustive()}

      <KebabMenu
        cnV2Eligibility={cnV2Eligibility}
        manualPaymentEligibility={manualPaymentEligibility}
      />
      <ButtonClose />
    </Flex>
  )
}

const ButtonFinalizeAndSend = () => (
  <FinalizeAndSendModal
    showViewButton={false}
    trigger={({ onClick, disabled }) => (
      <ButtonReviewAndSend
        onClick={onClick}
        disabled={disabled}
        data-testid="header.button.finalize-and-send"
      />
    )}
  />
)
